import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Check, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePlatform, platformStyle, setOrgStyles, UNIFIED } from "../lib/ridersPlatform";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

// A branded platform switcher shared across all rider-performance panels.
// Selecting a platform re-scopes every rider dashboard to that platform's data.
export const RiderPlatformSwitcher = ({ canEdit = false, showUnified = false }) => {
  const [platform, setActive] = usePlatform();
  const [platforms, setPlatforms] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#2563EB");
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.ridersConfig();
      setPlatforms(r.platforms || []);
      setOrgStyles(r.styles || {});
    } catch { /* keep defaults */ }
  };
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.ridersConfig();
        if (!alive) return;
        setPlatforms(r.platforms || []);
        setOrgStyles(r.styles || {});
      } catch { /* keep defaults */ }
    })();
    return () => { alive = false; };
  }, []);

  const addPlatform = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      const r = await api.addRidersPlatform(n);
      setPlatforms(r.platforms || []);
      await api.setRidersPlatformStyle(n, color, logoFile);
      await load();
      setActive(n);
      setAddOpen(false); setName(""); setColor("#2563EB"); setLogoFile(null);
      toast.success(`تمت إضافة منصة "${n}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإضافة"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-2 w-full" data-testid="rider-platform-switcher">
      {showUnified && (() => {
        const active = platform === UNIFIED;
        return (
          <motion.button
            data-testid="platform-pill-unified"
            onClick={() => setActive(UNIFIED)}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-all border w-fit"
            style={active
              ? { background: "linear-gradient(90deg,#1e3a8a,#2563eb)", color: "#fff", borderColor: "#1d4ed8", boxShadow: "0 4px 14px rgba(37,99,235,.35)" }
              : { background: "#fff", color: "#334155", borderColor: "#cbd5e1" }}
          >
            <LayoutGrid className="h-4 w-4" />
            العرض الموحّد — كل التطبيقات
          </motion.button>
        );
      })()}
      <div className="flex items-center gap-1.5 flex-wrap">
        {platforms.map((p) => {
        const st = platformStyle(p);
        const active = p === platform;
        return (
          <motion.button
            key={p}
            data-testid={`platform-pill-${p}`}
            onClick={() => setActive(p)}
            whileTap={{ scale: 0.95 }}
            className="relative inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all border"
            style={active
              ? { background: st.bg, color: st.text, borderColor: st.color, boxShadow: `0 0 0 3px ${st.ring}` }
              : { background: "#fff", color: "#64748b", borderColor: "#e2e8f0" }}
          >
            <span aria-hidden>
              {st.logo
                ? <img src={st.logo} alt={p} className="h-5 w-5 rounded object-contain" />
                : <span>{st.emoji}</span>}
            </span>
            {p}
            {active && <Check className="h-3.5 w-3.5" style={{ color: st.color }} />}
          </motion.button>
        );
      })}
      {canEdit && (
        <button
          data-testid="platform-add-button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-slate-500 border border-dashed border-slate-300 hover:border-blue-400 hover:text-blue-600 transition"
        >
          <Plus className="h-4 w-4" /> منصة
        </button>
      )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent dir="rtl" className="font-body">
          <DialogHeader>
            <DialogTitle className="font-heading text-start">إضافة منصة توصيل جديدة</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="platform-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="مثال: جاهز، مرسول، تويو…"
              onKeyDown={(e) => e.key === "Enter" && addPlatform()}
            />
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <span>لون المنصة:</span>
                <input data-testid="platform-color-input" type="color" value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 rounded-md border border-slate-200 cursor-pointer bg-white p-0.5" />
              </label>
              <div className="flex items-center gap-2">
                <input id="platform-logo-input" data-testid="platform-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
                <Button type="button" variant="outline" size="sm" className="h-9 gap-1.5"
                  onClick={() => document.getElementById("platform-logo-input").click()}>
                  <Plus className="h-4 w-4" /> شعار (اختياري)
                </Button>
              </div>
            </div>
            {/* معاينة */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">معاينة:</span>
              <span className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border"
                style={{ background: `${color}1A`, color, borderColor: color }}>
                {logoFile
                  ? <img src={URL.createObjectURL(logoFile)} alt="" className="h-5 w-5 rounded object-contain" />
                  : <span aria-hidden>📦</span>}
                {name.trim() || "المنصة"}
              </span>
            </div>
            <p className="text-xs text-slate-500">ستظهر المنصة الجديدة كتبويب مستقل بلونها وشعارها، وبياناتها منفصلة تماماً عن باقي المنصات.</p>
          </div>
          <DialogFooter>
            <Button data-testid="platform-save-button" onClick={addPlatform} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700">
              {saving ? "جارٍ الحفظ…" : "إضافة"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
